import { isEmpty, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  AlexGoLanguage,
  Election,
  ElectionMember,
  JVPBootstrapPublicationInfo,
  JVPUser,
} from 'Utils/apiTypes';
import { logInfo } from 'Utils/datadogLogs';
import logger from 'Utils/logger';

import { BEST_FIT_SCORE, FAIR_FIT_SCORE, GREAT_FIT_SCORE, labelOptions, PAGE_SLUGS } from './constants';
import { Specialty, HouseholdMember, InvalidPlanMessageKey } from '../../types/entities';

export const getCookie = (key: string): string => {
  const cookieKeyValue = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${key}`)) || '';
  const [, value] = cookieKeyValue.split('=');
  return value;
};

export const setCookie = (
  key: string,
  value: string | boolean,
  domain?: string,
  path?: string,
  expirationInMinutes?: number,
  secure?: boolean,
) => {
  let cookieContents = `${key}=${value}`;
  if (domain) {
    cookieContents += `;domain=${domain}`;
  }
  if (path) {
    cookieContents += `;path=${path}`;
  }
  if (expirationInMinutes) {
    const exp = new Date();
    const expireTime = exp.getTime() + expirationInMinutes * 60 * 1000;
    exp.setTime(expireTime);
    cookieContents += `;expires=${exp.toUTCString()}`;
  }
  if (secure === true) {
    cookieContents += `;secure`;
  }
  document.cookie = cookieContents;
};

export const defaultCookieDomain = () => {
  if (process.env.NODE_ENV === 'development') {
    return undefined;
  }

  if (window.location.hostname.includes('jellydevs.com')) {
    return 'jellydevs.com';
  }

  return 'myalex.com';
};

export const deleteCookie = (key: string, domain?: string, path?: string) => {
  let cookieContents = `${key}=`;
  if (domain) {
    cookieContents += `;domain=${domain}`;
  }
  if (path) {
    cookieContents += `;path=${path}`;
  }

  cookieContents += '; expires = Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = cookieContents;
};

const AUTH_COOKIE_NAME_RE = /^(_legacy_)?auth0\.([\w.]+\.)?is\.authenticated$/;

export type AuthCookieValue = 'true' | 'undefined' | 'false' | 'other';

// Cookie values are reduced to an enum so a listing is always safe to log.
export interface AuthCookie {
  name: string;
  domain: string;
  value: AuthCookieValue;
  expires_in_s: number | null;
}

export const authCookieValue = (raw: unknown): AuthCookieValue => {
  if (raw === undefined || raw === 'undefined') return 'undefined';
  if (raw === 'true' || raw === true) return 'true';
  if (raw === 'false' || raw === false) return 'false';
  return 'other';
};

// Parses every pair itself so duplicate names (host-only plus shared-domain) are all visible;
// document.cookie cannot tell their domains apart.
const listAuthCookiesFromDocument = (): AuthCookie[] => {
  try {
    return (document.cookie || '').split(';').flatMap((pair) => {
      const trimmed = pair.trim();
      const eq = trimmed.indexOf('=');
      const name = eq === -1 ? trimmed : trimmed.slice(0, eq);
      if (!AUTH_COOKIE_NAME_RE.test(name)) return [];
      const value = eq === -1 ? '' : trimmed.slice(eq + 1);
      return [{ name, domain: 'unknown', value: authCookieValue(value), expires_in_s: null }];
    });
  } catch {
    return [];
  }
};

// Lists every Auth0 "is authenticated" cookie, with domain and expiry where the Cookie Store API
// is available. Never rejects.
export const listAuthCookies = async (): Promise<AuthCookie[]> => {
  try {
    const store = (window as any).cookieStore;
    if (store && typeof store.getAll === 'function') {
      const items: any[] = await store.getAll();
      const now = Date.now();
      return (items || [])
        .filter((cookie) => AUTH_COOKIE_NAME_RE.test(cookie?.name ?? ''))
        .map((cookie) => {
          let domain: string;
          // null means host-only; Firefox omits the field entirely, which says nothing.
          if (cookie.domain === null) domain = '(host-only)';
          else if (cookie.domain === undefined) domain = 'unknown';
          else domain = String(cookie.domain);
          const expires_in_s =
            typeof cookie.expires === 'number' ? Math.round((cookie.expires - now) / 1000) : null;
          return { name: cookie.name, domain, value: authCookieValue(cookie.value), expires_in_s };
        });
    }
  } catch {
    // fall through to document.cookie
  }
  return listAuthCookiesFromDocument();
};

// Bounds the wait on a pending listAuthCookies() so callers on a navigation path add at most
// timeoutMs; past that, falls back to the synchronous document.cookie listing.
export const listAuthCookiesWithin = async (
  pending: Promise<AuthCookie[]>,
  timeoutMs = 50,
): Promise<AuthCookie[]> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<AuthCookie[]>((resolve) => {
      timer = setTimeout(() => resolve(listAuthCookiesFromDocument()), timeoutMs);
    });
    return await Promise.race([pending.catch(() => []), timeout]);
  } catch {
    return [];
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const sourcePrefixForJvp = 'guest__';
export const stripSourceComponent = (jvpId: string) => jvpId.replace(sourcePrefixForJvp, '');

// Languages a client got before `alex_go_languages` existed, when the only signal
// was the `show_language_toggle` config flag. See resolveEnabledLanguages.
const LEGACY_TOGGLE_LANGUAGES: AlexGoLanguage[] = ['english', 'spanish'];

// Order is preserved — it's the customer's configured order.
export const normalizeLanguages = (values: unknown): AlexGoLanguage[] => {
  if (!Array.isArray(values)) return [];
  const normalized: AlexGoLanguage[] = [];
  values.forEach((value) => {
    if (typeof value !== 'string') return;
    const language = value.trim().toLowerCase() as AlexGoLanguage;
    if (language && !normalized.includes(language)) normalized.push(language);
  });
  return normalized;
};

// `show_language_toggle` is a last resort, not a gate — it's only ever true for
// english_and_spanish upstream, so gating on it would suppress the selector for a
// customer who enabled e.g. [english, chinese]. It exists for non-Builder clients
// that never reach bootstrap; remove once no live client depends on it.
export const resolveEnabledLanguages = (
  bootstrapLanguages: AlexGoLanguage[] | undefined,
  showLanguageToggle: boolean,
): AlexGoLanguage[] => {
  if (bootstrapLanguages && bootstrapLanguages.length > 0) return bootstrapLanguages;
  return showLanguageToggle ? LEGACY_TOGGLE_LANGUAGES : [];
};

// NOTE: AAPI requires identifiers that are exactly 32 chars so we have to strip
//   hyphens from our external id component.
//  We'll maintain this format for all external ids for consistency.
export const makeNewCommExternalId = () => `${sourcePrefixForJvp}${uuidv4().replace(/-/g, '')}`;

export const parseJvpUserId = (user: JVPUser) =>
  user.auth0_uuid || `${user.external_source}__${user.external_id}`;

/**
 * The publication a partner launched the employee into, from the AAPI user's
 * `customer_product_key` (`<employer>/<publication slug>`). Null when there was no partner
 * launch or its slug matches neither publication.
 */
export const getLaunchedPublicationKey = (
  active: JVPBootstrapPublicationInfo | null | undefined,
  upcoming: JVPBootstrapPublicationInfo | null | undefined,
  customerProductKey: string | null | undefined,
): 'active' | 'upcoming' | null => {
  if (customerProductKey) {
    const publicationSlug = customerProductKey.split('/')[1];
    if (publicationSlug === active?.publication.slug) {
      return 'active';
    }
    if (publicationSlug === upcoming?.publication.slug) {
      return 'upcoming';
    }
  }

  return null;
};

const urlSegmentIsPageSlug = (segment: string): boolean => {
  return PAGE_SLUGS.includes(segment);
};

/**
 * Employer slug is always that first part of the URL before any page slugs
 * It is used for analytics events and bootstrapping
 * ie: if we have a customer with a segment like "lyondellbasell/edison" we expect "lyondellbasell"
 */
export const getEmployerSlugFromLocation = (location: Location): string => {
  const nonEmptyPathParts = location.pathname.split('/').filter(Boolean);
  return nonEmptyPathParts[0];
};

/**
 * Segment slug for cases where we have like lyondellbasell/edison
 * We want the last part (domain.tld/(employer)(/segment)?(/page)? of the URL path excluding page slug segments
 * If the url excluding page slug is only 1 part, we do not have a segment slug
 */
export const getSegmentSlugFromLocation = (location: Location): string => {
  const nonEmptyPathParts = location.pathname.split('/').filter(Boolean);

  // filter out path parts that resemble a page slug.
  const slugParts = nonEmptyPathParts.filter((seg) => {
    return !urlSegmentIsPageSlug(seg);
  });

  return slugParts.length > 1 ? slugParts[slugParts.length - 1] : '';
};

export const getFullAlexUrl = (location: Location, segmentSlug: string | undefined): string =>
  location.href + (segmentSlug || '');

export const formatPathForInsights = (path, fixedPath) => {
  if (path === fixedPath) {
    return 'landing';
  }

  const parts = path && path?.split('/');

  // Filter out any empty parts resulting from leading/trailing slashes
  const filteredParts = parts && parts?.filter((part) => part !== '');

  // If there's only one part, return it as is
  if (filteredParts?.length <= 1) {
    return filteredParts[0] || '';
  }

  // Join all parts except the first one
  return filteredParts?.slice(1).join('/');
};

export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
};

export const moveSelectedPlanToTop = (availablePlans, result) => {
  const preselectedPlanId = result?.election?.plan_details?.jv_medical_plan_id;

  // Reorder availablePlans array with the preselected plan first
  const reorderedPlans = [...availablePlans];

  // Find the index of the preselected plan
  const preselectedIndex = reorderedPlans?.findIndex((plan) => plan?.plan?.external_id === preselectedPlanId);

  if (preselectedIndex !== -1) {
    // Remove the preselected plan from its current position
    const [preselectedPlan] = reorderedPlans?.splice(preselectedIndex, 1) || [];

    // Insert the preselected plan at the beginning of the array
    reorderedPlans?.unshift(preselectedPlan);
  }

  return reorderedPlans;
};

export const getInvalidPlanMessageKey = (
  tierCode: string[],
  hasElection: boolean,
  householdMembers: HouseholdMember[],
  electionMembersEnabled: boolean,
  matchedPlan?: Election,
): InvalidPlanMessageKey | null => {
  // null represents the case where we don't have to invalidate the user's existing selection and show them a message

  if (!hasElection) return null;

  if (!matchedPlan) return InvalidPlanMessageKey.NoLongerEligible;

  if (electionMembersEnabled) {
    // If jvpUser ever becomes the source of truth in the FE instead of household.members
    // for user & dependent data, it would enable us to compare apples-to-apples
    // re: external_id and not have to strip the "guest__" part from it in the FE via stripSourceComponent()

    const dependentAndSigOtherIds = householdMembers
      .filter((m) => !m.policyholder)
      .map((m) => stripSourceComponent(m.external_id));

    const electionDependentAndSigOtherIds = (matchedPlan.election_members || [])
      .filter((m: ElectionMember) => m.relationship !== 'self' && m.external_id !== null)
      .map((m: ElectionMember) => m.external_id as string);

    if (
      dependentAndSigOtherIds.length !== electionDependentAndSigOtherIds.length ||
      !isEqual(new Set(dependentAndSigOtherIds), new Set(electionDependentAndSigOtherIds))
    ) {
      return InvalidPlanMessageKey.HouseholdMembersChanged;
    }
  }

  if (isEmpty(tierCode)) return null;

  if (parseInt(matchedPlan.tier_code || '0', 10) !== parseInt(tierCode[0], 10)) {
    return InvalidPlanMessageKey.HouseholdMembersChanged;
  }

  return null;
};

export const fetchShowVideoStatus = (insuranceType: string): string => {
  const insuranceTypeStatusMapping = {
    accident: 'show_accident_video',
    add: 'show_add_video',
    commuter: 'show_commuter_video',
    critical: 'show_critical_illness_video',
    dental: 'show_dental_video',
    employee_assistance_program: 'show_eap_video',
    financial_wellness: 'show_financial_wellness_video',
    fsa: 'show_fsa_video',
    fsa_dependent: 'show_dcfsa_video',
    fsa_medical: 'show_fsa_video',
    hospital: 'show_hospital_indemnity_video',
    hra: 'show_hra_video',
    hsa: 'show_hsa_video',
    legal: 'show_legal_video',
    life: 'show_group_life_video',
    lpfsa: 'show_lpfsa_video',
    ltd: 'show_ltd_video',
    retirement: 'show_retirement_video',
    '401k': 'show_retirement_video',
    '403b': 'show_retirement_video',
    std: 'show_std_video',
    teledoc: 'show_telemedicine_video',
    vision: 'show_vision_video',
    wellness: 'show_wellness_video',
  };

  return insuranceTypeStatusMapping[insuranceType] || 'other';
};

export const sortPlansAlphabetically = (availablePlans) =>
  [...availablePlans]?.sort((a, b) =>
    a?.plan?.plan_marketing_name?.localeCompare(b?.plan?.plan_marketing_name),
  );

// Forced logouts in BC are traced to Go copying a missing or disagreeing auth cookie onto the
// shared domain. Logs only when Go's own client cookies are missing, not "true", or disagree.
const logBcHandoffAuthMismatch = (
  cookies: AuthCookie[],
  context: {
    willCopyValue: unknown;
    idTokenPresent: boolean;
    idTokenExp: number | undefined;
    idTokenLookupFailed: boolean;
    loginRenewal: 'not_needed' | 'renewed' | 'failed';
    planYear: unknown;
    hasSegment: boolean;
  },
): void => {
  const goClientId = window._env_?.REACT_APP_AUTH0_CLIENT_ID;
  const goCookies = cookies.filter((cookie) => Boolean(goClientId) && cookie.name.includes(goClientId!));
  const agreeOnTrue =
    goCookies.some((cookie) => cookie.value === 'true') &&
    new Set(goCookies.map((cookie) => cookie.value)).size === 1;
  if (agreeOnTrue) return;
  logInfo('bc_handoff_auth_mismatch', {
    auth_cookies: cookies,
    auth_cookie_count: cookies.length,
    will_copy_value: authCookieValue(context.willCopyValue),
    id_token_present: context.idTokenPresent,
    id_token_seconds_to_expiry:
      typeof context.idTokenExp === 'number' ? Math.round(context.idTokenExp - Date.now() / 1000) : null,
    id_token_lookup_failed: context.idTokenLookupFailed,
    login_renewal: context.loginRenewal,
    employer_slug: getEmployerSlugFromLocation(window.location),
    plan_year: context.planYear,
    has_segment: context.hasSegment,
    unified_session_id: getCookie('unified_session_id'),
  });
};

const BC_HANDOFF_KEY = 'bcHandoff';
const BC_BOUNCE_WINDOW_MS = 60000;

// Leaves a marker so the next Go load can tell it was reached by coming straight back from BC.
// It carries the session id because alex_api deletes the shared unified_session_id cookie on
// every BC boot, so a bounced user arrives back on Go without it.
const markBcHandoff = (wasAuthenticated: boolean): void => {
  try {
    window.sessionStorage.setItem(
      BC_HANDOFF_KEY,
      JSON.stringify({
        at: Date.now(),
        was_authenticated: wasAuthenticated,
        unified_session_id: getCookie('unified_session_id'),
      }),
    );
  } catch {
    // sessionStorage can be unavailable (privacy modes)
  }
};

const clearBcHandoffMarker = (): void => {
  try {
    window.sessionStorage.removeItem(BC_HANDOFF_KEY);
  } catch {
    // sessionStorage can be unavailable (privacy modes)
  }
};

// A page restored from bfcache does not re-run Go's load effects, so its stale marker would
// otherwise be read as a bounce on the next real load.
let bfcacheClearRegistered = false;
const registerBfcacheClear = (): void => {
  if (bfcacheClearRegistered) return;
  bfcacheClearRegistered = true;
  try {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) clearBcHandoffMarker();
    });
  } catch {
    // ignore
  }
};
registerBfcacheClear();

const ALLOWED_REFERRER_HOST_RE =
  /^((start|start-[a-z0-9-]+|www|qa|review|staging|local|login)\.(myalex|jellydevs)\.com|review-\d+-picwell-app\.jellydevs\.com)$/;

// Only allowlisted hosts are reported by name; never a path or query.
const referrerHost = (referrer: string): string => {
  if (!referrer) return 'none';
  try {
    const { hostname } = new URL(referrer);
    return ALLOWED_REFERRER_HOST_RE.test(hostname) ? hostname : 'other';
  } catch {
    return 'other';
  }
};

// Reports, once per trip, a user landing back on Go within a minute of being sent to BC: the
// visible symptom of BC logging them out. Back/forward navigation is a deliberate return, not a bounce.
// Must be called before the app navigates, so employer_slug reflects the page the user landed on.
export const detectBcBounce = async (isAuthenticated: boolean): Promise<void> => {
  try {
    const employerSlug = getEmployerSlugFromLocation(window.location);
    registerBfcacheClear();
    const raw = window.sessionStorage.getItem(BC_HANDOFF_KEY);
    if (raw === null) return;
    clearBcHandoffMarker();

    const marker = JSON.parse(raw);
    if (!marker || typeof marker.at !== 'number' || !Number.isFinite(marker.at)) return;
    const elapsedMs = Date.now() - marker.at;
    if (elapsedMs < 0 || elapsedMs > BC_BOUNCE_WINDOW_MS) return;

    const navigationType = (
      performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
    )?.type;
    if (navigationType === 'back_forward') return;

    const cookies = await listAuthCookies();
    logInfo('go_bc_bounce', {
      elapsed_ms: elapsedMs,
      was_authenticated: Boolean(marker.was_authenticated),
      is_authenticated_now: isAuthenticated,
      navigation_type: navigationType ?? 'unknown',
      referrer_host: referrerHost(document.referrer),
      auth_cookies: cookies,
      auth_cookie_count: cookies.length,
      employer_slug: employerSlug,
      unified_session_id: typeof marker.unified_session_id === 'string' ? marker.unified_session_id : null,
    });
  } catch {
    // diagnostics must never affect the app
  }
};

// Every value document.cookie holds under exactly this name. A name can appear more than once
// (Go's host-only cookie plus a shared-domain copy), and the stale copy is often listed first.
const getCookieValues = (key: string): string[] =>
  document.cookie
    .split('; ')
    .filter((cookie) => cookie.split('=')[0] === key)
    .map((cookie) => cookie.slice(key.length + 1));

const TOKEN_EXPIRY_MARGIN_SECONDS = 60;

const isUsableToken = (claims: { __raw?: string; exp?: number } | undefined): boolean =>
  Boolean(claims?.__raw) &&
  (typeof claims?.exp !== 'number' || claims.exp - Date.now() / 1000 > TOKEN_EXPIRY_MARGIN_SECONDS);

// Go's Auth0 login cookie. Go also copies it onto the shared domain so BC can see it.
export const goAuthCookieName = (): string =>
  `auth0.${window._env_?.REACT_APP_AUTH0_CLIENT_ID}.is.authenticated`;

const BC_HANDOFF_RESUME_KEY = 'resumeBcHandoff';

// Remembers, across Auth0's login redirect, that the user was on their way to BC.
export const markBcHandoffResume = (): void => {
  try {
    window.sessionStorage.setItem(BC_HANDOFF_RESUME_KEY, 'true');
  } catch {
    // sessionStorage can be unavailable (privacy modes)
  }
};

// True once, right after a re-login that interrupted a trip to BC.
export const takeBcHandoffResume = (): boolean => {
  try {
    const shouldResume = window.sessionStorage.getItem(BC_HANDOFF_RESUME_KEY) === 'true';
    window.sessionStorage.removeItem(BC_HANDOFF_RESUME_KEY);
    return shouldResume;
  } catch {
    return false;
  }
};

export interface BcHandoffLogin {
  // Renews Go's login without user interaction; rejects when Auth0 needs the user to log in.
  renewLogin: () => Promise<unknown>;
  // Sends the user through Auth0's login and back, to resume the trip to BC.
  loginAgain: () => void;
}

export const handleBcNavigate = async (
  jvp,
  isDemoMode,
  planYear,
  validatedSegmentSlug,
  bcUrlHash,
  GO_GUEST_ID,
  isAuthenticated,
  getIdTokenClaims,
  getBCPath,
  login: BcHandoffLogin,
) => {
  if (jvp.sessionId) {
    setCookie('session_id', jvp.sessionId, defaultCookieDomain(), '/', 2);
  }

  const authCookieName = goAuthCookieName();

  // Create a "guest user ID" cookie for guest users
  if (!isAuthenticated && jvp.userId) {
    // BC is expecting a 32 char identifier here
    const goGuestIdForCookie = stripSourceComponent(jvp.userId);
    setCookie(GO_GUEST_ID, goGuestIdForCookie, defaultCookieDomain(), '/');
  }

  // A shared auth cookie left by an earlier logged-in visit would tell BC this guest is logged in.
  if (!isAuthenticated) {
    deleteCookie(authCookieName, defaultCookieDomain(), '/');
  }

  // Create a JWT cookie for AAPI/BC for logged in users
  if (isAuthenticated) {
    // Started before the token await so it resolves while that await is pending.
    const pendingAuthCookies = listAuthCookies();
    let claims: { __raw?: string; exp?: number } | undefined;
    let idTokenLookupFailed = false;
    claims = await getIdTokenClaims().catch((tokenError) => {
      idTokenLookupFailed = true;
      logger.error(tokenError);
      return undefined;
    });
    // Settled before anything below is written, so the log shows what Go found.
    const authCookies = await listAuthCookiesWithin(pendingAuthCookies).catch(() => []);
    const foundIdToken = claims;

    // Go can still show the user as logged in after its own auth cookie or ID token has gone
    // stale; handing that state to BC sends the user back to Go or signs them out of BC.
    let loginRenewal: 'not_needed' | 'renewed' | 'failed' = 'not_needed';
    if (!getCookieValues(authCookieName).includes('true') || !isUsableToken(claims)) {
      try {
        await login.renewLogin();
        claims = await getIdTokenClaims();
        loginRenewal = isUsableToken(claims) ? 'renewed' : 'failed';
      } catch {
        loginRenewal = 'failed';
      }
    }

    try {
      logBcHandoffAuthMismatch(authCookies, {
        willCopyValue: loginRenewal === 'failed' ? undefined : 'true',
        idTokenPresent: Boolean(foundIdToken?.__raw),
        idTokenExp: foundIdToken?.exp,
        idTokenLookupFailed,
        loginRenewal,
        planYear,
        hasSegment: Boolean(validatedSegmentSlug),
      });
    } catch {
      // diagnostics must never affect navigation
    }

    if (loginRenewal === 'failed') {
      login.loginAgain();
      return;
    }

    setCookie('jv_jwt', claims!.__raw!, defaultCookieDomain(), '/', 1, true);
    // Only ever a real `true`, so a stale shared copy is overwritten rather than carried forward.
    setCookie(authCookieName, 'true', defaultCookieDomain(), '/', 1440);
  }

  const bcPath = getBCPath(isDemoMode, planYear?.toString() || '', validatedSegmentSlug, bcUrlHash);

  window.DD_RUM?.addAction('bc_redirect', {
    segment_slug: validatedSegmentSlug,
    plan_year: planYear,
    is_demo: isDemoMode,
    is_authenticated: isAuthenticated,
  });

  // Lets alex_api tell a trip from Go apart from a reload or bookmark, and which login state BC
  // should follow. alex_api deletes it on the first BC load.
  setCookie('go_handoff', isAuthenticated ? 'logged_in' : 'guest', defaultCookieDomain(), '/', 2);

  window.onbeforeunload = null;
  markBcHandoff(isAuthenticated);
  window.location.assign(bcPath);
};

// The BC (audio) experience is English-only. Centralized so every entry point (product
// modal, single-product config, deep-link hash) resets the locale the same way.
export const forceEnglishForBcPath = (locale: string, setLocale: (locale: string) => void): void => {
  if (locale !== 'en-US') {
    setLocale('en-US');
  }
};

export const getPlanFitField = (score: number) => {
  // will get the field name based on plan score here and retrive its contentful text from result page
  let fieldName = labelOptions?.poor;

  if (score >= BEST_FIT_SCORE) {
    // BEST
    fieldName = labelOptions?.best;
  } else if (score >= GREAT_FIT_SCORE) {
    // GREAT
    fieldName = labelOptions?.great;
  } else if (score >= FAIR_FIT_SCORE) {
    // FAIR
    fieldName = labelOptions?.fair;
  } else {
    // POOR
    fieldName = labelOptions?.poor;
  }

  return fieldName;
};

// When accident_no_premiums is true, hide the premium from UI for accident plan
export const hideAccidentPremiums = (plan, builderGoSettings) =>
  plan?.plan_type === 'accident' && builderGoSettings?.accident_no_premiums;

export const normalizePlanName = (input?: string) => {
  if (!input || typeof input !== 'string') return '';

  const trimmed = input?.trim();
  const words = trimmed?.split(/\s+/);
  const lastWord = words[words.length - 1]?.toLowerCase();

  return lastWord === 'plan' ? trimmed : `${trimmed} plan`;
};

// Returns the appropriate content key for the coverage disclaimer text based on availability of coverage to spouse, partner, and children.
export const getCoverageDisclaimerKey = ({
  availableToSpouse,
  availableToPartner,
  availableToChildren,
  prefix,
}): string => {
  if (availableToSpouse && availableToPartner && availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_all`;
  }
  if (availableToSpouse && availableToPartner && !availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_partners`;
  }
  if (availableToSpouse && !availableToPartner && availableToChildren) {
    return `${prefix}.coverage_disclaimer_spouses_and_children`;
  }
  if (availableToSpouse && !availableToPartner && !availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_spouses`;
  }
  if (!availableToSpouse && availableToPartner && availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_children`;
  }
  if (!availableToSpouse && availableToPartner && !availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_partners_only`;
  }
  if (!availableToSpouse && !availableToPartner && availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_children_only`;
  }
  if (!availableToSpouse && !availableToPartner && !availableToChildren) {
    return `${prefix}.coverage_disclaimer_available_to_none`;
  }
  return '';
};

export const getProviderDisplayType = (
  specialties?: Specialty[],
  providerTypes?: string[],
  separator: string = ' • ',
): string => {
  if (!specialties || specialties.length === 0) {
    return providerTypes?.[0] ?? 'No Specialty';
  }

  const taxonomies: string[] = specialties.flatMap((spec) =>
    [spec.taxonomy_1, spec.taxonomy_2].filter((t): t is string => Boolean(t)),
  );

  if (taxonomies.length > 0) {
    return taxonomies.slice(0, 2).join(separator); // max 2
  }

  return specialties[0].provider_type || providerTypes?.[0] || 'No Specialty';
};

// custom plan selection sends { waived: true } to JVP, but we must not overwrite
// a return user's real plan (P1) in Redux. Preserve existing elections when JVP returns
// waived-only (no plan_details), Redux already has a real plan, and the user did not
// explicitly waive (isCoverageWaived distinguishes custom plan from an intentional waive).
export const resolveElections = (
  fetchedElections: Election[],
  currentElections: Election[],
  isCoverageWaived: boolean,
): Election[] => {
  const jvpIsWaivedOnly = fetchedElections?.every((e) => e.waived && !e.plan_details);
  const reduxHasRealPlan = currentElections?.some((e) => e.plan_details);
  return jvpIsWaivedOnly && reduxHasRealPlan && !isCoverageWaived ? currentElections : fetchedElections;
};

export const getTierCodeDescription = (tierCode: string) => {
  const tierDescriptions = {
    '10': 'Just EE',
    '20': 'EE + Spouse',
    '30': 'EE + Children',
    '40': 'EE + 2 Children',
    '50': 'EE + 3 Children',
    '55': 'EE + 4 or more Children',
    '60': 'EE + Spouse + Children',
    '70': 'EE + Spouse + 2 Children',
    '80': 'EE + Spouse + 3 Children',
    '90': 'EE + Spouse + 4 or more Children',
  };

  return tierDescriptions[tierCode] || '';
};
